import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Grid,
  FormHelperText,
  Alert,
  Divider,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import { useSchedulesSchedulesPartialUpdateMutation, useSalonsRetrieveQuery, useSchedulesBreaksByTeamMemberRetrieveQuery, useSchedulesBreaksCreateMutation, useSchedulesBreaksDestroyMutation, useSchedulesBreaksPartialUpdateMutation } from '../../../../services/GLOWClient';
import { normalizeDayName, getBackendDayFormat } from '../../../../utils/dayUtils';
import dayjs from 'dayjs';
import MuiDialog from '../../../../SystemDesign/MuiDialog';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useTheme } from '@mui/material/styles';
import theme from '../../../../theme';
import CoffeeIcon from '@mui/icons-material/Coffee';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddBreak from '../breaks/AddBreak';
import BreaksList from '../breaks/BreaksList';
import Notification from '../../../common/Notification';

const UpdateSchedule = ({ open, onClose, onSuccess, schedule }) => {
  const [updateSchedule, { isLoading }] = useSchedulesSchedulesPartialUpdateMutation();
  const [createBreak, { isLoading: isCreatingBreak }] = useSchedulesBreaksCreateMutation();
  const [updateBreak, { isLoading: isUpdatingBreak }] = useSchedulesBreaksPartialUpdateMutation();
  const [deleteBreak, { isLoading: isDeletingBreak }] = useSchedulesBreaksDestroyMutation();
  
  // Fetch salon details to get opening hours
  const { data: salonData, isLoading: salonLoading } = useSalonsRetrieveQuery(
    schedule?.salon?.salon_id ? { salonId: schedule.salon.salon_id } : undefined,
    { skip: !schedule?.salon?.salon_id }
  );
  
  // Form state
  const [formData, setFormData] = useState({
    team_member: '',
    salon: '',
    day_of_week: '',
    start_time: null,
    end_time: null
  });
  
  const [errors, setErrors] = useState({});
  
  // États pour la gestion des pauses
  const [isAddBreakDialogOpen, setIsAddBreakDialogOpen] = useState(false);
  const [selectedBreakData, setSelectedBreakData] = useState(null);
  const [breaksDialogOpen, setBreaksDialogOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  
  // États pour la nouvelle gestion des pauses multiples
  const [breakSwitches, setBreakSwitches] = useState({});
  
  // Time picker popover state
  const [timePickerAnchor, setTimePickerAnchor] = useState(null);
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [breakData, setBreakData] = useState({});
  const [confirmedBreaks, setConfirmedBreaks] = useState({});
  // Track which break is being edited
  const [editingBreak, setEditingBreak] = useState(null);
  // State for delete confirmation dialog
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    breakToDelete: null
  });
  
  // State to track if delete operation is in progress
  const [isDeleting, setIsDeleting] = useState(false);
  
  // État pour les notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });
  
  // Fonction pour afficher une notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  // Fonction pour fermer la notification
  const closeNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  // Récupérer les pauses du membre d'équipe si disponible
  const { data: breaks, isLoading: breaksLoading, refetch: refetchBreaks } = useSchedulesBreaksByTeamMemberRetrieveQuery(
    schedule?.team_member?.team_member_id ? 
    { teamMemberId: schedule.team_member.team_member_id } : 
    undefined,
    { 
      skip: !schedule?.team_member?.team_member_id,
      refetchOnMountOrArgChange: true 
    }
  );

  // Préparer les données pour le composant BreaksList
  const breaksArray = Array.isArray(breaks) ? breaks : breaks ? [breaks] : [];
  
  // Filtrer les pauses pour le jour actuel
  const dayBreaks = breaksArray.filter(breakItem => 
    breakItem.day_of_week.toLowerCase() === schedule?.day_of_week.toLowerCase()
  );

  // Days of the week options
  const daysOfWeek = [
    { value: 'Lundi', label: 'Lundi' },
    { value: 'Mardi', label: 'Mardi' },
    { value: 'Mercredi', label: 'Mercredi' },
    { value: 'Jeudi', label: 'Jeudi' },
    { value: 'Vendredi', label: 'Vendredi' },
    { value: 'Samedi', label: 'Samedi' },
    { value: 'Dimanche', label: 'Dimanche' }
  ];
  
  // Initialize form data when schedule changes
  useEffect(() => {
    if (schedule) {
      setFormData({
        team_member: schedule.team_member?.team_member_id || '',
        salon: schedule.salon?.salon_id || '',
        day_of_week: schedule.day_of_week || '',
        start_time: schedule.start_time ? dayjs(`2023-01-01T${schedule.start_time}`) : null,
        end_time: schedule.end_time ? dayjs(`2023-01-01T${schedule.end_time}`) : null
      });
    }
  }, [schedule]);
  
  // Initialize break data separately to avoid dependency cycle
  useEffect(() => {
    if (schedule && schedule.day_of_week) {
      // Initialize break switches
      setBreakSwitches(prev => ({
        ...prev,
        [schedule.day_of_week]: dayBreaks.length > 0
      }));
      
      // Initialize break data
      setBreakData(prev => ({
        ...prev,
        [schedule.day_of_week]: {
          start_time: '',
          end_time: '',
          description: 'Pause'
        }
      }));
      
      // Convert existing breaks to confirmed breaks format
      if (dayBreaks.length > 0) {
        const formattedBreaks = dayBreaks.map(breakItem => ({
          id: breakItem.break_id,
          start_time: breakItem.start_time.substring(0, 5), // Format HH:MM
          end_time: breakItem.end_time.substring(0, 5), // Format HH:MM
          description: breakItem.description || 'Pause',
          // Store the proper backend day format to ensure consistency
          backend_day: schedule.day_of_week === 'Dimanche' ? 'sun' : schedule.day_of_week.toLowerCase()
        }));
        
        setConfirmedBreaks(prev => ({
          ...prev,
          [schedule.day_of_week]: formattedBreaks
        }));
      } else {
        setConfirmedBreaks(prev => ({
          ...prev,
          [schedule.day_of_week]: []
        }));
      }
    }
  }, [schedule, dayBreaks.length]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle time picker changes
  const handleTimeChange = (name, time) => {
    setFormData(prev => ({
      ...prev,
      [name]: time
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Close the popover
    setTimePickerAnchor(null);
    setActiveTimePicker(null);
  };
  
  // Handle direct time input changes (typing) with auto-formatting
  const handleTimeInputChange = (name, value) => {
    if (!value || value === 'fermé') {
      setFormData(prev => ({
        ...prev,
        [name]: null
      }));
      return;
    }

    // Remove non-numeric characters except colon
    let cleanValue = value.replace(/[^\d:]/g, '');
    
    // Auto-format as user types
    if (cleanValue.length === 1 && parseInt(cleanValue) > 2) {
      cleanValue = '0' + cleanValue + ':';
    } else if (cleanValue.length === 2 && !cleanValue.includes(':')) {
      const hours = parseInt(cleanValue);
      if (hours > 23) {
        cleanValue = '23:';
      } else {
        cleanValue = cleanValue + ':';
      }
    } else if (cleanValue.length === 3 && cleanValue.endsWith(':')) {
      // Keep as is - user typed HH:
    } else if (cleanValue.length === 4 && cleanValue.includes(':')) {
      const [hours, minutes] = cleanValue.split(':');
      const minuteValue = parseInt(minutes);
      if (minuteValue > 5) {
        cleanValue = hours + ':0' + minutes;
      }
    } else if (cleanValue.length === 5) {
      const [hours, minutes] = cleanValue.split(':');
      const hourValue = parseInt(hours);
      const minuteValue = parseInt(minutes);
      
      if (hourValue > 23) {
        cleanValue = '23:' + minutes;
      }
      if (minuteValue > 59) {
        cleanValue = hours + ':59';
      }
    }

    // Validate final format
    const timeRegex = /^([01]?[0-9]|2[0-3]):?([0-5]?[0-9]?)$/;
    if (!timeRegex.test(cleanValue)) {
      return;
    }

    const timeValue = cleanValue ? dayjs(`2023-01-01T${cleanValue}`) : null;
    setFormData(prev => ({
      ...prev,
      [name]: timeValue
    }));
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle time picker open
  const handleTimePickerOpen = (event, timeType) => {
    setTimePickerAnchor(event.currentTarget);
    setActiveTimePicker({ timeType });
  };
  
  // Handle break time changes
  const handleBreakTimeChange = (timeType, value) => {
    const timeValue = value ? value.format('HH:mm') : '';
    const breakTimeType = timeType === 'break_start' ? 'start_time' : 'end_time';
    
    setBreakData(prev => ({
      ...prev,
      [schedule?.day_of_week]: {
        ...prev[schedule?.day_of_week],
        [breakTimeType]: timeValue
      }
    }));
    
    // Close the popover
    setTimePickerAnchor(null);
    setActiveTimePicker(null);
  };

  // Handle direct break time input changes (typing) with auto-formatting
  const handleBreakTimeInputChange = (breakId, timeType, value) => {
    if (!value || value === 'fermé') {
      setBreakData(prev => ({
        ...prev,
        [schedule?.day_of_week]: {
          ...prev[schedule?.day_of_week],
          [timeType]: ''
        }
      }));
      return;
    }

    // Remove non-numeric characters except colon
    let cleanValue = value.replace(/[^\d:]/g, '');
    
    // Auto-format as user types
    if (cleanValue.length === 1 && parseInt(cleanValue) > 2) {
      cleanValue = '0' + cleanValue + ':';
    } else if (cleanValue.length === 2 && !cleanValue.includes(':')) {
      const hours = parseInt(cleanValue);
      if (hours > 23) {
        cleanValue = '23:';
      } else {
        cleanValue = cleanValue + ':';
      }
    } else if (cleanValue.length === 3 && cleanValue.endsWith(':')) {
      // Keep as is - user typed HH:
    } else if (cleanValue.length === 4 && cleanValue.includes(':')) {
      const [hours, minutes] = cleanValue.split(':');
      const minuteValue = parseInt(minutes);
      if (minuteValue > 5) {
        cleanValue = hours + ':0' + minutes;
      }
    } else if (cleanValue.length === 5) {
      const [hours, minutes] = cleanValue.split(':');
      const hourValue = parseInt(hours);
      const minuteValue = parseInt(minutes);
      
      if (hourValue > 23) {
        cleanValue = '23:' + minutes;
      }
      if (minuteValue > 59) {
        cleanValue = hours + ':59';
      }
    }

    // Validate final format
    const timeRegex = /^([01]?[0-9]|2[0-3]):?([0-5]?[0-9]?)$/;
    if (!timeRegex.test(cleanValue)) {
      return;
    }

    setBreakData(prev => ({
      ...prev,
      [schedule?.day_of_week]: {
        ...prev[schedule?.day_of_week],
        [timeType]: cleanValue
      }
    }));
  };
  
  // Helper function to convert time string (HH:MM or HH:MM:SS) to minutes
  const timeToMinutes = (timeStr) => {
    if (!timeStr || timeStr === 'fermé') return 0;
    const [hours, minutes] = timeStr.split(':');
    return parseInt(hours) * 60 + parseInt(minutes || 0);
  };

  const normalizeInterval = (startTime, endTime, { allowEqualAsFullDay = false } = {}) => {
    const start = timeToMinutes(startTime);
    let end = timeToMinutes(endTime);
    if (end < start || (allowEqualAsFullDay && end === start)) {
      end += 24 * 60;
    }
    return { start, end };
  };

  const isWithinSalonHours = (employeeStart, employeeEnd, salonOpen, salonClose) => {
    const salonInterval = normalizeInterval(salonOpen, salonClose, { allowEqualAsFullDay: true });
    const employeeInterval = normalizeInterval(employeeStart, employeeEnd);
    return (
      employeeInterval.start >= salonInterval.start
      && employeeInterval.end <= salonInterval.end
      && employeeInterval.end > employeeInterval.start
    );
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.day_of_week) {
      newErrors.day_of_week = 'Le jour est requis';
    }
    
    if (!formData.start_time) {
      newErrors.start_time = 'L\'heure de début est requise';
    }
    
    if (!formData.end_time) {
      newErrors.end_time = 'L\'heure de fin est requise';
    }
    
    if (formData.start_time && formData.end_time && 
        formData.start_time.format('HH:mm') === formData.end_time.format('HH:mm')) {
      newErrors.end_time = 'L\'heure de fin doit être différente de l\'heure de début';
    }
    
    // Validate against salon opening hours
    if (salonData && salonData.opening_hours && formData.day_of_week && formData.start_time && formData.end_time) {
      // Handle day format inconsistency (Sunday as 'sun' instead of 'sunday')
      let dayLowerCase = formData.day_of_week.toLowerCase();
      // Convert 'dimanche' to 'sun' for backend consistency
      if (dayLowerCase === 'dimanche') {
        dayLowerCase = 'sun';
      }
      
      const salonHours = salonData.opening_hours[dayLowerCase];
      
      if (salonHours) {
        // Check if salon is open on this day
        if (salonHours.ouverture === 'fermé' || salonHours.fermeture === 'fermé') {
          newErrors.day_of_week = 'Le salon est fermé ce jour';
        } else {
          const employeeStartTime = formData.start_time.format('HH:mm');
          const employeeEndTime = formData.end_time.format('HH:mm');

          if (!isWithinSalonHours(employeeStartTime, employeeEndTime, salonHours.ouverture, salonHours.fermeture)) {
            newErrors.start_time = `Les horaires doivent être compris entre ${salonHours.ouverture} et ${salonHours.fermeture}`;
          }
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    if (!schedule || !schedule.schedule_id) {
      console.error('No schedule selected for update');
      return;
    }
    
    try {
      // Format the data according to the API requirements
      const updateData = {
        scheduleId: schedule.schedule_id,
        patchedScheduleRequest: {
          day_of_week: formData.day_of_week,
          start_time: formData.start_time ? formData.start_time.format('HH:mm:ss') : undefined,
          end_time: formData.end_time ? formData.end_time.format('HH:mm:ss') : undefined
        }
      };
      
      // Only include team_member and salon if they are changed and not empty
      if (formData.team_member && formData.team_member !== schedule.team_member?.team_member_id) {
        updateData.patchedScheduleRequest.team_member = formData.team_member;
      }
      
      if (formData.salon && formData.salon !== schedule.salon?.salon_id) {
        updateData.patchedScheduleRequest.salon = formData.salon;
      }
      
      // Always include break_requests in the update data
      const breakRequests = [];
      
      // Add break requests if there are confirmed breaks
      if (confirmedBreaks[schedule.day_of_week]?.length > 0) {
        // Format breaks for the API
        confirmedBreaks[schedule.day_of_week].forEach(breakItem => {
          breakRequests.push({
            team_member: schedule.team_member?.team_member_id,
            salon: schedule.salon?.salon_id,
            day_of_week: breakItem.backend_day, // Use the stored backend day format
            start_time: `${breakItem.start_time}:00`, // Add seconds to match API format
            end_time: `${breakItem.end_time}:00`, // Add seconds to match API format
            description: breakItem.description
          });
        });
      }
      
      // Always include break_requests in the update data
      updateData.patchedScheduleRequest.break_requests = breakRequests;
      
      const result = await updateSchedule(updateData).unwrap();
      
      // Call success callback and close dialog
      onSuccess && onSuccess();
      onClose();
      showNotification('Horaire mis à jour avec succès');
    } catch (error) {
      console.error('Failed to update schedule:', error);
      // Handle API errors
      // 1) Show non_field_errors as a snackbar if present
      const nonFieldErrors =
        error?.data?.non_field_errors || // common DRF shape
        error?.data?.errors?.non_field_errors; // sometimes nested under errors

      if (nonFieldErrors) {
        const message = Array.isArray(nonFieldErrors)
          ? nonFieldErrors.join(' ')
          : String(nonFieldErrors);
        if (message.trim()) {
          showNotification(message, 'error');
        }
      }

      // 2) Surface field errors in the form
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      } else if (!nonFieldErrors) {
        // 3) Fallback generic error if nothing else matched
        showNotification('Erreur lors de la mise à jour de l\'horaire', 'error');
      }
    }
  };

  // Contenu du dialogue
  const dialogContent = (
    <Box>
      {errors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.general}
        </Alert>
      )}
      
      {salonData && salonData.opening_hours && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontWeight: 'bold',
              mb: 2,
              color: theme.colors.primary && theme.colors.primary[600] ? theme.colors.primary[600] : '#3a5b41'
            }}>
              <BusinessIcon sx={{ 
                mr: 1, 
                color: theme.colors.primary && theme.colors.primary[500] ? theme.colors.primary[500] : '#416548' 
              }} />
              Horaires d'ouverture du salon
            </Typography>
            
            <Paper elevation={0} sx={{ 
              p: 2, 
              backgroundColor: theme.colors.primary && theme.colors.primary[50] ? 
                `rgba(${theme.colors.primary[50].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.5)` : 
                'rgba(240, 244, 241, 0.5)', 
              borderRadius: 2, 
              border: `1px solid ${theme.colors.primary && theme.colors.primary[100] ? theme.colors.primary[100] : '#dce6df'}` 
            }}>
              <Grid container spacing={2}>
                {Object.entries(salonData.opening_hours).map(([day, hours]) => {
                  const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                  const isCurrentDay = dayName.toLowerCase() === formData.day_of_week.toLowerCase() || 
                                      (dayName.toLowerCase() === 'dimanche' && formData.day_of_week.toLowerCase() === 'sun');
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: isCurrentDay ? 
                          (theme.colors.primary && theme.colors.primary[100] ? 
                            `rgba(${theme.colors.primary[100].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.7)` : 
                            'rgba(220, 230, 223, 0.7)') : 
                          'transparent',
                        border: isCurrentDay ? 
                          `1px solid ${theme.colors.primary && theme.colors.primary[200] ? theme.colors.primary[200] : '#b9cdc0'}` : 
                          'none'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: isCurrentDay ? 'bold' : 'medium', 
                          color: isCurrentDay ? 
                            (theme.colors.primary && theme.colors.primary[700] ? theme.colors.primary[700] : '#2f4a36') : 
                            'text.primary'
                        }}>
                          {dayName}:
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          fontWeight: isCurrentDay ? 'bold' : 'normal',
                          color: hours.ouverture === 'fermé' ? 
                            (theme.colors.error && theme.colors.error.main ? theme.colors.error.main : '#f44336') : 
                            'text.secondary'
                        }}>
                          {hours.ouverture === 'fermé' ? 'Fermé' : 
                          `${hours.ouverture} - ${hours.fermeture}`}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        </>
      )}
      
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontWeight: 'bold',
            mb: 2,
            color: theme.colors.primary && theme.colors.primary[600] ? theme.colors.primary[600] : '#3a5b41'
          }}>
            <CalendarMonthIcon sx={{ 
              mr: 1, 
              color: theme.colors.primary && theme.colors.primary[500] ? theme.colors.primary[500] : '#416548' 
            }} />
            Jour: {schedule?.day_of_week}
          </Typography>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Heure de début"
              value={formData.start_time ? formData.start_time.format('HH:mm') : ''}
              size="small"
              fullWidth
              placeholder="09:00"
              error={!!errors.start_time}
              helperText={errors.start_time}
              onChange={(e) => handleTimeInputChange('start_time', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ color: theme.colors.primary[300] }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: 1.5,
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ccc',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.colors.primary[500],
                  }
                }
              }}
            />
            <IconButton
              onClick={(e) => handleTimePickerOpen(e, 'start_time')}
              size="small"
              sx={{
                color: theme.colors.primary[500],
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <AccessTimeIcon />
            </IconButton>
          </Box>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Heure de fin"
              value={formData.end_time ? formData.end_time.format('HH:mm') : ''}
              size="small"
              fullWidth
              placeholder="18:00"
              error={!!errors.end_time}
              helperText={errors.end_time}
              onChange={(e) => handleTimeInputChange('end_time', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccessTimeIcon sx={{ color: theme.colors.primary[300] }} />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  borderRadius: 1.5,
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ccc',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.colors.primary[500],
                  }
                }
              }}
            />
            <IconButton
              onClick={(e) => handleTimePickerOpen(e, 'end_time')}
              size="small"
              sx={{
                color: theme.colors.primary[500],
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <AccessTimeIcon />
            </IconButton>
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          
          {/* Section des pauses */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              fontWeight: 'bold',
              mb: 2,
              color: theme.colors.secondary && theme.colors.secondary[600] ? theme.colors.secondary[600] : '#9c27b0'
            }}>
              <CoffeeIcon sx={{ 
                mr: 1, 
                color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0' 
              }} />
              Pauses
            </Typography>
            
            {/* No switch mode as requested by the user */}
            <Paper elevation={0} sx={{ 
              p: 2, 
              mt: 2,
              backgroundColor: theme.colors.secondary && theme.colors.secondary[100] ? 
                `rgba(${theme.colors.secondary[100].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` : 
                'rgba(156, 39, 176, 0.05)', 
              borderRadius: 2, 
              border: `1px dashed ${theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0'}` 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CoffeeIcon sx={{ mr: 1, fontSize: 18, color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0' }} />
                  Ajouter une pause
                </Typography>
                <Chip 
                  label={`${confirmedBreaks[schedule?.day_of_week]?.length || 0} pause(s)`} 
                  size="small" 
                  sx={{ 
                    fontWeight: 'medium',
                    borderColor: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0',
                    color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0'
                  }}
                  variant="outlined" 
                />
              </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            label="Heure de début"
                            value={breakData[schedule?.day_of_week]?.start_time || ''}
                            fullWidth
                            size="small"
                            placeholder="12:00"
                            onChange={(e) => handleBreakTimeInputChange(null, 'start_time', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccessTimeIcon sx={{ color: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0' }} />
                                </InputAdornment>
                              )
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fff',
                                borderRadius: 1.5,
                                '& fieldset': {
                                  borderColor: '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#ccc',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0',
                                }
                              }
                            }}
                          />
                          <IconButton
                            onClick={(e) => handleTimePickerOpen(e, 'break_start')}
                            size="small"
                            sx={{
                              color: theme.colors.primary[500],
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.04)'
                              }
                            }}
                          >
                            <AccessTimeIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            label="Heure de fin"
                            value={breakData[schedule?.day_of_week]?.end_time || ''}
                            fullWidth
                            size="small"
                            placeholder="13:00"
                            onChange={(e) => handleBreakTimeInputChange(null, 'end_time', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccessTimeIcon sx={{ color: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0' }} />
                                </InputAdornment>
                              )
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#fff',
                                borderRadius: 1.5,
                                '& fieldset': {
                                  borderColor: '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#ccc',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0',
                                }
                              }
                            }}
                          />
                          <IconButton
                            onClick={(e) => handleTimePickerOpen(e, 'break_end')}
                            size="small"
                            sx={{
                              color: theme.colors.primary[500],
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.04)'
                              }
                            }}
                          >
                            <AccessTimeIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          label="Description"
                          fullWidth
                          size="small"
                          value={breakData[schedule?.day_of_week]?.description || ''}
                          onChange={(e) => {
                            setBreakData(prev => ({
                              ...prev,
                              [schedule?.day_of_week]: {
                                ...prev[schedule?.day_of_week],
                                description: e.target.value
                              }
                            }));
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CoffeeIcon sx={{ color: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0' }} />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: theme.colors.secondary && theme.colors.secondary[400] ? theme.colors.secondary[400] : '#9c27b0',
                            '&:hover': {
                              backgroundColor: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#7b1fa2',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: 'rgba(0, 0, 0, 0.12)',
                            },
                            fontWeight: 'medium',
                            px: 2
                          }}
                          size="small"
                          startIcon={<CoffeeIcon />}
                          disabled={!breakData[schedule?.day_of_week]?.start_time || !breakData[schedule?.day_of_week]?.end_time}
                          onClick={async () => {
                            // Validate break times
                            if (!breakData[schedule?.day_of_week]?.start_time || !breakData[schedule?.day_of_week]?.end_time) {
                              setNotification({
                                open: true,
                                message: 'Veuillez spécifier les heures de début et de fin',
                                severity: 'error'
                              });
                              return;
                            }
                            
                            const startTimeStr = breakData[schedule?.day_of_week].start_time;
                            const endTimeStr = breakData[schedule?.day_of_week].end_time;
                            
                            // Convert strings to dayjs objects if they aren't already
                            const startTime = typeof startTimeStr === 'string' ? 
                              dayjs(`2023-01-01T${startTimeStr}`) : startTimeStr;
                            const endTime = typeof endTimeStr === 'string' ? 
                              dayjs(`2023-01-01T${endTimeStr}`) : endTimeStr;
                            
                            // Format times properly
                            const formattedStartTime = startTime.format ? startTime.format('HH:mm') : startTimeStr;
                            const formattedEndTime = endTime.format ? endTime.format('HH:mm') : endTimeStr;
                            
                            if (endTime.isBefore && endTime.isBefore(startTime)) {
                              setNotification({
                                open: true,
                                message: 'L\'heure de fin doit être après l\'heure de début',
                                severity: 'error'
                              });
                              return;
                            }
                            
                            try {
                              // Check if we're editing an existing break or creating a new one
                              if (editingBreak && editingBreak.id && typeof editingBreak.id === 'number') {
                                // We're updating an existing break
                                const updateRequest = {
                                  breakId: editingBreak.id,
                                  patchedBreakRequest: {
                                    team_member: schedule.team_member?.team_member_id,
                                    salon: schedule.salon?.salon_id,
                                    day_of_week: getBackendDayFormat(schedule?.day_of_week),
                                    start_time: `${formattedStartTime}:00`,
                                    end_time: `${formattedEndTime}:00`,
                                    description: breakData[schedule?.day_of_week].description || 'Pause'
                                  }
                                };
                                
                                // Call the RTK query mutation to update the break
                                const result = await updateBreak(updateRequest).unwrap();
                                
                                // If successful, update the break in the UI
                                if (result) {
                                  const updatedBreak = {
                                    id: result.break_id || editingBreak.id,
                                    start_time: formattedStartTime,
                                    end_time: formattedEndTime,
                                    description: breakData[schedule?.day_of_week].description || 'Pause',
                                    backend_day: getBackendDayFormat(schedule?.day_of_week)
                                  };
                                  
                                  // Add the updated break to the confirmed breaks
                                  const updatedBreaks = [...(confirmedBreaks[schedule?.day_of_week] || []), updatedBreak];
                                  
                                  setConfirmedBreaks(prev => ({
                                    ...prev,
                                    [schedule?.day_of_week]: updatedBreaks
                                  }));
                                  
                                 
                                }
                              } else {
                                // We're creating a new break
                                const breakRequest = {
                                  team_member: schedule.team_member?.team_member_id,
                                  salon: schedule.salon?.salon_id,
                                  day_of_week: getBackendDayFormat(schedule?.day_of_week),
                                  start_time: `${formattedStartTime}:00`,
                                  end_time: `${formattedEndTime}:00`,
                                  description: breakData[schedule?.day_of_week].description || 'Pause'
                                };
                                
                                // Call the RTK query mutation to create the break
                                const result = await createBreak({ breakRequest }).unwrap();
                                
                                // If successful, add the break to the UI
                                if (result) {
                                  const newBreak = {
                                    id: result.break_id || Date.now(),
                                    start_time: formattedStartTime,
                                    end_time: formattedEndTime,
                                    description: breakData[schedule?.day_of_week].description || 'Pause',
                                    backend_day: getBackendDayFormat(schedule?.day_of_week)
                                  };
                                  
                                  // Create a new array instead of modifying the existing one
                                  const updatedBreaks = [...(confirmedBreaks[schedule?.day_of_week] || []), newBreak];
                                  
                                  setConfirmedBreaks(prev => ({
                                    ...prev,
                                    [schedule?.day_of_week]: updatedBreaks
                                  }));
                                  
                               
                                }
                              }
                              
                              // Reset the form for a new break and clear editing state
                              setBreakData(prev => ({
                                ...prev,
                                [schedule?.day_of_week]: {
                                  start_time: '',
                                  end_time: '',
                                  description: 'Pause'
                                }
                              }));
                              
                              setEditingBreak(null);
                              
                              // Refresh the breaks list
                              refetchBreaks();
                            } catch (error) {
                              console.error('Failed to save break:', error);
                              setNotification({
                                open: true,
                                message: editingBreak ? 'Erreur lors de la mise à jour de la pause' : 'Erreur lors de la création de la pause',
                                severity: 'error'
                              });
                            }
                          }}
                        >
                          {editingBreak ? 'Mettre à jour la pause' : 'Confirmer la pause'}
                        </Button>
                      </Grid>
                    </Grid>
                    
                    {/* Display confirmed breaks */}
                    {confirmedBreaks[schedule?.day_of_week] && confirmedBreaks[schedule?.day_of_week].length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Divider sx={{ mb: 2 }}>
                          <Chip 
                            label="Pauses confirmées" 
                            size="small" 
                            sx={{ 
                              fontWeight: 'medium',
                              borderColor: theme.colors.secondary && theme.colors.secondary[300] ? theme.colors.secondary[300] : '#9c27b0',
                              color: theme.colors.secondary && theme.colors.secondary[500] ? theme.colors.secondary[500] : '#9c27b0'
                            }}
                            variant="outlined"
                          />
                        </Divider>
                        
                        {confirmedBreaks[schedule?.day_of_week].map((breakItem, index) => (
                          <Paper 
                            key={breakItem.id} 
                            elevation={0} 
                            sx={{ 
                              p: 1.5, 
                              mb: 1.5, 
                              backgroundColor: theme.colors.secondary && theme.colors.secondary[100] ? 
                                `rgba(${theme.colors.secondary[100].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.2)` : 
                                'rgba(156, 39, 176, 0.05)',
                              border: `1px solid ${theme.colors.secondary && theme.colors.secondary[200] ? theme.colors.secondary[200] : '#ce93d8'}`,
                              borderRadius: 1.5,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CoffeeIcon sx={{ mr: 1.5, fontSize: 16, color: theme.colors.secondary && theme.colors.secondary[400] ? theme.colors.secondary[400] : '#9c27b0' }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {breakItem.start_time} - {breakItem.end_time}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {breakItem.description}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex' }}>
                              <IconButton 
                                size="small" 
                                sx={{ 
                                  color: theme.colors.primary && theme.colors.primary[500] ? theme.colors.primary[500] : '#416548',
                                  '&:hover': {
                                    backgroundColor: theme.colors.primary && theme.colors.primary[500] ? 
                                      `rgba(${theme.colors.primary[500].replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` :
                                      'rgba(65, 101, 72, 0.1)',
                                  },
                                  mr: 0.5
                                }}
                                onClick={async () => {
                                  try {
                                    // Set the break being edited
                                    setEditingBreak(breakItem);
                                    
                                    // Set the break data to edit
                                    setBreakData(prev => ({
                                      ...prev,
                                      [schedule?.day_of_week]: {
                                        start_time: breakItem.start_time,
                                        end_time: breakItem.end_time,
                                        description: breakItem.description
                                      }
                                    }));
                                    
                                    // If the break has a backend ID (not just a local ID), update it in the backend
                                    if (breakItem.id && typeof breakItem.id === 'number') {
                                      // Remove the break from confirmed breaks first (we'll add it back after update)
                                      setConfirmedBreaks(prev => ({
                                        ...prev,
                                        [schedule?.day_of_week]: prev[schedule?.day_of_week].filter(b => b.id !== breakItem.id)
                                      }));
                                      
                                      setNotification({
                                        open: true,
                                        message: 'Modification de la pause en cours...',
                                        severity: 'info'
                                      });
                                    } else {
                                      // For breaks without a backend ID, just remove from UI
                                      setConfirmedBreaks(prev => ({
                                        ...prev,
                                        [schedule?.day_of_week]: prev[schedule?.day_of_week].filter(b => b.id !== breakItem.id)
                                      }));
                                      
                                      setNotification({
                                        open: true,
                                        message: 'Modification de la pause',
                                        severity: 'info'
                                      });
                                    }
                                  } catch (error) {
                                    console.error('Error preparing break for edit:', error);
                                    setNotification({
                                      open: true,
                                      message: 'Erreur lors de la préparation de la modification',
                                      severity: 'error'
                                    });
                                  }
                                }}
                              >
                                <Tooltip title="Modifier cette pause">
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </Tooltip>
                              </IconButton>
                              <IconButton 
                                size="small" 
                                sx={{ 
                                  color: theme.colors.error && theme.colors.error.main ? theme.colors.error.main : '#f44336',
                                  '&:hover': {
                                    backgroundColor: theme.colors.error && theme.colors.error.main ? 
                                      `rgba(${theme.colors.error.main.replace('#', '').match(/../g).map(hex => parseInt(hex, 16)).join(', ')}, 0.1)` :
                                      'rgba(244, 67, 54, 0.1)',
                                  }
                                }}
                                onClick={() => {
                                  // Show confirmation dialog instead of deleting immediately
                                  setDeleteConfirmDialog({
                                    open: true,
                                    breakToDelete: breakItem
                                  });
                                }}
                              >
                                <Tooltip title="Supprimer cette pause">
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </Tooltip>
                              </IconButton>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
             </Paper>
             </Box>
            </Grid>
          </Grid>
          </Box>
          
     
  );

  return (
    <>
      <MuiDialog
        open={open}
        onExternalClose={onClose}
        icon={<CalendarMonthIcon />}
        title="Modifier l'horaire"
        subtitle={`${schedule?.team_member?.user_details?.full_name || 'Sans nom'} - ${schedule?.day_of_week}`}
        content={dialogContent}
        existAction={true}
        actionTitle={isLoading ? "Chargement..." : "Mettre à jour"}
        actionFunction={handleSubmit}
        isLoading={isLoading}
        maxWidth="md"
      />
      
      {/* Add Break Dialog */}
      {selectedBreakData && (
        <AddBreak
          open={isAddBreakDialogOpen}
          onClose={() => setIsAddBreakDialogOpen(false)}
          onSuccess={() => {
            setIsAddBreakDialogOpen(false);
            refetchBreaks();
          }}
          teamMember={selectedBreakData.teamMember}
          salon={selectedBreakData.salon}
          dayOfWeek={selectedBreakData.dayOfWeek}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <MuiDialog
        open={deleteConfirmDialog.open}
        onExternalClose={() => setDeleteConfirmDialog({ open: false, breakToDelete: null })}
        icon={<DeleteOutlineIcon />}
        title="Confirmation de suppression"
        subtitle="Suppression de pause"
        content={
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette pause ?
          </Typography>
        }
        existAction={true}
        actionTitle={isDeleting ? "Suppression..." : "Supprimer"}
        actionFunction={async () => {
          const breakItem = deleteConfirmDialog.breakToDelete;
          if (!breakItem) return;
          
          setIsDeleting(true);
          try {
            // If the break has a numeric ID, it exists in the backend and needs to be deleted there
            if (breakItem.id && typeof breakItem.id === 'number') {
              await deleteBreak({ breakId: breakItem.id }).unwrap();
            }
            
            // Remove the break from the UI
            setConfirmedBreaks(prev => ({
              ...prev,
              [schedule?.day_of_week]: prev[schedule?.day_of_week].filter(b => b.id !== breakItem.id)
            }));
            
            // Refresh the breaks list
            refetchBreaks();
            
            // Close the dialog
            setDeleteConfirmDialog({ open: false, breakToDelete: null });
          } catch (error) {
            console.error('Failed to delete break:', error);
            setNotification({
              open: true,
              message: 'Erreur lors de la suppression de la pause',
              severity: 'error'
            });
          } finally {
            setIsDeleting(false);
          }
        }}
        isLoading={isDeleting}
        maxWidth="xs"
      />
      
      {/* Notification Component */}
      <Notification 
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={closeNotification}
        autoHideDuration={3000}
      />
      
      {/* Time Picker Popover */}
      <Popover
        open={Boolean(timePickerAnchor)}
        anchorEl={timePickerAnchor}
        onClose={() => {
          setTimePickerAnchor(null);
          setActiveTimePicker(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }
        }}
      >
        {activeTimePicker && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: '#666' }}>
              Sélectionner l'heure {activeTimePicker.timeType === 'break_start' ? 'de début de pause' : 
              activeTimePicker.timeType === 'break_end' ? 'de fin de pause' :
              activeTimePicker.timeType === 'start_time' ? 'de début' : 'de fin'}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MultiSectionDigitalClock
                value={(() => {
                  const { timeType } = activeTimePicker;
                  
                  if (timeType === 'break_start' || timeType === 'break_end') {
                    const breakTimeType = timeType === 'break_start' ? 'start_time' : 'end_time';
                    const timeValue = breakData[schedule?.day_of_week]?.[breakTimeType] || '';
                    return timeValue ? dayjs(`2023-01-01T${timeValue}`) : dayjs().startOf('day').hour(12);
                  } else {
                    const timeValue = formData[timeType];
                    return timeValue || dayjs().startOf('day').hour(9);
                  }
                })()}
                onChange={(newValue) => {
                  if (activeTimePicker) {
                    const { timeType } = activeTimePicker;
                    if (timeType === 'break_start' || timeType === 'break_end') {
                      handleBreakTimeChange(timeType, newValue);
                    } else {
                      handleTimeChange(timeType, newValue);
                    }
                  }
                }}
                ampm={false}
                views={['hours', 'minutes']}
                timeSteps={{ hours: 1, minutes: 1 }}
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  '& .MuiMultiSectionDigitalClock-root': {
                    maxWidth: 200
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default UpdateSchedule;
